import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Dataset,
    DatasetDtoGet,
    DatasetDtoPatch,
    DatasetDtoPost,
    DatasetFileTypeOption,
    DatasetFiles,
    ErrorContext,
    FileAddingDtoPost,
    FileAddingResponseDto,
    FileDeleteResponseDto,
    ListResponse,
    RunStep,
    SearchOptions,
    TrainingMethod,
    UserDetail,
    WorkflowRunStatusEvent,
    WorkflowV1Read,
    WorkflowV1Run,
} from '@ca/core/models';
import { environment } from '@ca/environment/environment';
import { getFileNameWithExtension, getRunSteps, getSelectedModules } from '@ca/utils';
import { CacheBucket, HttpCacheManager, withCache } from '@ngneat/cashew';
import clone from 'clone-deep';
import { Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { createAnnotationsDictsByTrainingImagesAndFilterByClasses, hasAnnotationsForAllClasses } from 'src/utils/dataset/dataset-utils';
import { WorkflowV1Service } from '.';
import * as FileUtils from '../../../utils/file/file-utils';
import { MACHINE_LEARNING_SERVICE_URL, WORKFLOW_SETTINGS_SERVICE_URL } from '../../shared/constants/constants';
import { WorkflowType } from '../enums';
import { MlCacheType } from '../enums/ml-cache-type.enum';
import { errorContextToken } from '../interceptors/global-error.interceptor';
import {
    ListSearch,
    MlModelWithSegmentationSettings,
    MlModelWithSegmentationSettingsRead,
    MlModelWithSegmentationTrainingSettings,
    MlModelWithSegmentationTrainingSettingsRead,
} from '../models';
import { ImagesWithAnnotations, ModelWithFilesAndDatasetName } from '../models/images-with-annotations.model';
import { MLMeasurement } from '../models/measurement.model';
import { SegmentationOutputResult } from '../models/segmentation-output-result.model';
import { SegmentationResult } from '../models/segmentation-result.model';
import { TrainingOutputResult } from '../models/training-output-result.model';
import { TrainingResult } from '../models/training-result.model';
import { TrainingSettingsJson } from '../models/training-settings-json.model';
import { ValidateDatasetFileResult } from '../models/validate-dataset-file-result.model';
import { ValidateSegmentationFileResult } from '../models/validate-segmentation-file-result.model';
import { DomainFactoryService } from './domain-factory.service';
import { WorkflowRunQueueService } from './workflow-run-queue.service';
import { WorkflowRunService } from './workflow-run.service';
import { WorkflowRunnerStepBuilderService } from './workflow-runner-step-builder.service';
import { WorkflowStateService } from './workflow-state.service';

interface ContinueTrainingSettings {
    modelUrl: string;
    configUrl: string;
}

interface DatasetShare {
    role: 'Collaborator';
    userId: string;
}

@Injectable()
export class MachineLearningService {
    private readonly mlApiUrl = MACHINE_LEARNING_SERVICE_URL;
    readonly DATASETS_API_URL = `${this.mlApiUrl}/datasets`;
    private readonly resultsUrlPart = 'results';
    private readonly MACHINE_LEARNING_TRAINING_API_URL = `${this.mlApiUrl}/training`;
    private readonly MACHINE_LEARNING_SEGMENTATION_API_URL = `${this.mlApiUrl}/segmentation`;
    private readonly MACHINE_LEARNING_MEASUREMENT_API_URL = `/api/documents/measurementtypes`;
    private readonly headers = { 'Content-Type': 'application/json' };
    private readonly readAccessParams: HttpParams = new HttpParams().set('access', 'read');
    private buckets: Map<string, CacheBucket> = new Map<string, CacheBucket>();

    constructor(
        private domainFactory: DomainFactoryService,
        private http: HttpClient,
        private httpCacheManager: HttpCacheManager,
        private workflowRunnerStepBuilderService: WorkflowRunnerStepBuilderService,
        private workflowRunQueueService: WorkflowRunQueueService,
        private workflowRunService: WorkflowRunService,
        private workflowStateService: WorkflowStateService,
        private workflowV1Service: WorkflowV1Service,
    ) {
        this.buckets.set(MlCacheType.SEGMENTATION, new CacheBucket());
        this.buckets.set(MlCacheType.SEGMENTATIONS_WITHOUT_MODEL, new CacheBucket());
        this.buckets.set(MlCacheType.SEGMENTATION_TRAINING, new CacheBucket());
        this.buckets.set(MlCacheType.MODELS_WITH_SEGMENTATIONS, new CacheBucket());
        this.workflowRunQueueService.workflowCompleted$.subscribe((event: WorkflowRunStatusEvent) => {
            if (event.workflowType === WorkflowType.SEGMENTATION) {
                this.invalidateCacheOfSegmentations();
            } else if (event.workflowType === WorkflowType.SEGMENTATION_TRAINING) {
                this.invalidateCacheOfTrainings();
            }
        });
    }

    createDataset(datasetToCreate: DatasetDtoPost): Observable<Dataset> {
        return this.http.post(this.DATASETS_API_URL, datasetToCreate) as Observable<Dataset>;
    }

    updateDataset(datasetToUpdate: DatasetDtoPatch): Observable<Dataset> {
        return this.http.patch(this.DATASETS_API_URL, datasetToUpdate) as Observable<Dataset>;
    }

    shareDataset$(datasetId: string, userDetails: UserDetail[]): Observable<void> {
        const datasetShares: { datasetShares: DatasetShare[] } = { datasetShares: [] };
        for (const userDetail of userDetails) {
            datasetShares.datasetShares.push({ role: 'Collaborator', userId: userDetail.id } as DatasetShare);
        }
        return this.http.patch<void>(`${this.DATASETS_API_URL}/${datasetId}/shares`, datasetShares, { headers: this.headers });
    }

    fetchDatasetById$(datasetId: string): Observable<Dataset> {
        const params = new HttpParams().set('access', 'read');
        return this.http.get<Dataset>(`${this.DATASETS_API_URL}/${datasetId}`, { params }).pipe(map(Dataset.createInstance));
    }

    fetchDatasets(searchParams?: ListSearch): Observable<ListResponse<Dataset>> {
        let httpParams: HttpParams | undefined = undefined;
        if (searchParams) {
            httpParams = new HttpParams()
                .set('page[number]', searchParams.pageNumber.toString())
                .set('page[size]', searchParams.pageSize.toString())
                .set('sort', searchParams.sortBy)
                .set('search', searchParams.searchQuery);
        }
        return this.http
            .get<ListResponse<DatasetDtoGet>>(this.DATASETS_API_URL, {
                headers: this.headers,
                params: httpParams,
            })
            .pipe(
                map(result => {
                    const data = result.data.map(Dataset.createInstance);
                    return { data, meta: result.meta };
                }),
            );
    }

    searchDatasets(searchOptions: SearchOptions): Observable<ListResponse<Dataset>> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'MACHINE_LEARNING.ANNOTATE.ERROR_GETTING_DATASET' }] };
        const httpParams = new HttpParams()
            .set('page[number]', searchOptions.pageNumber.toString())
            .set('page[size]', searchOptions.pageSize.toString())
            .set('sort', searchOptions.sortBy)
            .set('search', searchOptions.searchTerm)
            .set('text', searchOptions.searchTerm)
            .set('users', searchOptions.users ?? '');
        return this.http.get<ListResponse<Dataset>>(`${this.DATASETS_API_URL}/monitoring/search`, {
            headers: this.headers,
            params: httpParams,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    fetchAllMachineLearningModels(modelForApplication?: boolean): Observable<MlModelWithSegmentationSettings[]> {
        const params: { modelForApplication: boolean } | {} = modelForApplication ? { modelForApplication } : {};
        return this.http
            .get<MlModelWithSegmentationSettingsRead[]>(`${this.MACHINE_LEARNING_SEGMENTATION_API_URL}/models`, { params })
            .pipe(
                map((models: MlModelWithSegmentationSettingsRead[]) =>
                    models.map((model: MlModelWithSegmentationSettingsRead) => model as MlModelWithSegmentationSettings),
                ),
            );
    }

    fetchMlModelsWithSegmentationTrainingSettings$(datasetId: string): Observable<MlModelWithSegmentationTrainingSettings[]> {
        return this.http
            .get<MlModelWithSegmentationTrainingSettingsRead[]>(`${this.MACHINE_LEARNING_TRAINING_API_URL}/models/ByDatasetId/${datasetId}`)
            .pipe(
                map((models: MlModelWithSegmentationTrainingSettingsRead[]) =>
                    models.map((model: MlModelWithSegmentationTrainingSettingsRead) => model as MlModelWithSegmentationTrainingSettings),
                ),
            );
    }

    deleteDataset(datasetId: string) {
        return this.http.delete(`${this.DATASETS_API_URL}/${datasetId}`);
    }

    addFilesToDataset$(datasetId: string, files: FileAddingDtoPost[]): Observable<{ datasetId: string; files: FileAddingResponseDto[] }> {
        return this.http.post<{ datasetId: string; files: FileAddingResponseDto[] }>(`${this.DATASETS_API_URL}/adds/files`, { datasetId, files });
    }

    removeFilesFromDataset$(datasetId: string, fileIds: string[], removeRelatedAnnotations = false): Observable<FileDeleteResponseDto[]> {
        return this.http.request<FileDeleteResponseDto[]>('delete', `${this.DATASETS_API_URL}/uploads/files`, {
            body: { datasetId, fileIds, removeRelatedAnnotations },
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        });
    }

    fetchDatasetContent$(datasetId: string, content?: DatasetFileTypeOption): Observable<DatasetFiles> {
        let params = new HttpParams().set('access', 'read');
        if (content != null) {
            params = params.append('datasetFileTypeOption', content);
        }
        return this.http.get<DatasetFiles>(`${this.DATASETS_API_URL}/${datasetId}/contents`, { params });
    }

    /**
     * If workflow is version with settings.json file (>= workflowInputSettingsJsonReleaseDate)
     * then we use settings.json file to provide all input parameters and put the file in the
     * first workflow input module.
     * If workflow is older than workflowInputSettingsJsonReleaseDate we use the old logic where
     * we search for appropriate modules and inputs for setting input parameters.
     */
    startTrainWorkflow$(
        workflow: WorkflowV1Run,
        dataset: Dataset,
        selectedTrainingMethod: TrainingMethod,
        selectedClasses: string[],
        trainingName: string,
        modelName: string,
        areClassesOverlapping: boolean = false,
        notifyWhenIsDone: boolean = false,
        zenCompatible: boolean = false,
    ): Observable<{ workflowRunId: string }> {
        return this.fetchDatasetContent$(dataset.datasetId).pipe(
            switchMap((datasetFiles: DatasetFiles) => {
                const { trainingImagesUrls, annotationUrls } = this.getTrainingImagesUrlsAndAnnotationUrls(datasetFiles, selectedClasses);

                if (trainingImagesUrls.length === 0 || annotationUrls.length === 0) {
                    return throwError({ message: 'MACHINE_LEARNING.TRAIN.NONE_OF_THE_IMAGES_HAS_ANNOTATIONS_FOR_ALL_SELECTED_CLASSES' });
                }

                if (this.isSettingsJsonWorkflow(workflow.createdAt)) {
                    return this.createTrainingSettingsFile$(
                        workflow.id,
                        dataset.datasetId,
                        !areClassesOverlapping,
                        zenCompatible,
                        trainingName,
                        modelName,
                        undefined,
                        selectedClasses,
                    ).pipe(
                        switchMap(trainingSettingsJson => {
                            const runSteps: RunStep[] = this.prepareTrainingWorkflowWithSettingsJson(workflow, trainingSettingsJson.uri, [
                                ...trainingImagesUrls,
                                ...annotationUrls,
                            ]);
                            return this.startTrainWithPreparedRunSteps$(
                                workflow,
                                runSteps,
                                trainingName,
                                notifyWhenIsDone,
                                WorkflowType.SEGMENTATION_TRAINING,
                                zenCompatible,
                                dataset.datasetId,
                                selectedTrainingMethod,
                                selectedClasses,
                                areClassesOverlapping,
                                undefined,
                                trainingSettingsJson.datasetVersionId,
                            );
                        }),
                    );
                } else {
                    const runSteps: RunStep[] = this.prepareTrainingWorkflowWithInputs(
                        workflow,
                        trainingImagesUrls,
                        annotationUrls,
                        selectedTrainingMethod,
                        areClassesOverlapping,
                        zenCompatible,
                    );
                    return this.startTrainWithPreparedRunSteps$(
                        workflow,
                        runSteps,
                        trainingName,
                        notifyWhenIsDone,
                        WorkflowType.SEGMENTATION_TRAINING,
                        zenCompatible,
                        dataset.datasetId,
                        selectedTrainingMethod,
                        selectedClasses,
                        areClassesOverlapping,
                    );
                }
            }),
        );
    }

    startContinueTrainWorkflow$(
        workflow: WorkflowV1Read,
        imagesWithAnnotations: ImagesWithAnnotations,
        modelWithSettings: MlModelWithSegmentationTrainingSettings,
        trainingName: string,
        modelName: string,
        trainingId: string,
        notifyWhenIsDone: boolean = false,
        zenCompatible: boolean = false,
        addMoreTrainingTime: boolean = false,
    ): Observable<{ workflowRunId: string }> {
        const { trainingImagesUrls, annotationUrls } = imagesWithAnnotations;
        if (trainingImagesUrls.length === 0 || annotationUrls.length === 0) {
            return throwError({ message: 'MACHINE_LEARNING.TRAIN.NONE_OF_THE_IMAGES_HAS_ANNOTATIONS_FOR_ALL_SELECTED_CLASSES' });
        }

        if (this.isSettingsJsonWorkflow(workflow.createdAt)) {
            return this.createTrainingSettingsFile$(
                workflow.id,
                modelWithSettings.datasetId,
                !modelWithSettings.classesOverlap,
                zenCompatible,
                trainingName,
                modelName,
                trainingId,
                modelWithSettings.classes!,
                addMoreTrainingTime,
            ).pipe(
                switchMap(trainingSettingsJson => {
                    const runSteps: RunStep[] = this.prepareTrainingWorkflowWithSettingsJson(
                        workflow,
                        trainingSettingsJson.uri,
                        [...trainingImagesUrls, ...annotationUrls],
                        { modelUrl: modelWithSettings.url, configUrl: modelWithSettings.configUrl },
                    );
                    return this.startTrainWithPreparedRunSteps$(
                        workflow,
                        runSteps,
                        trainingName,
                        notifyWhenIsDone,
                        WorkflowType.SEGMENTATION_CONTINUE_TRAINING,
                        zenCompatible,
                        modelWithSettings.datasetId,
                        modelWithSettings.trainingType,
                        modelWithSettings.classes,
                        modelWithSettings.classesOverlap,
                        trainingId,
                        trainingSettingsJson.datasetVersionId,
                    );
                }),
            );
        } else {
            const runSteps: RunStep[] = this.prepareTrainingWorkflowWithInputs(
                workflow,
                trainingImagesUrls,
                annotationUrls,
                modelWithSettings.trainingType!,
                modelWithSettings.classesOverlap,
                zenCompatible,
                { modelUrl: modelWithSettings.url, configUrl: modelWithSettings.configUrl },
            );
            return this.startTrainWithPreparedRunSteps$(
                workflow,
                runSteps,
                trainingName,
                notifyWhenIsDone,
                WorkflowType.SEGMENTATION_CONTINUE_TRAINING,
                zenCompatible,
                modelWithSettings.datasetId,
                modelWithSettings.trainingType,
                modelWithSettings.classes,
                modelWithSettings.classesOverlap,
                trainingId,
            );
        }
    }

    /**
     * If workflow is version with settings.json file (>= workflowInputSettingsJsonReleaseDate)
     * then we use settings.json file to provide all input parameters and put the file in the
     * first workflow input module.
     * If workflow is older than workflowInputSettingsJsonReleaseDate we use the old logic where
     * we search for appropriate modules and inputs for setting input parameters.
     */
    startSegmentWorkflow$(
        workflow: WorkflowV1Run,
        modelUrl: string,
        configUrl: string,
        imagesToSegmentUrls: string[],
        segmentationName: string,
        notifyWhenIsDone: boolean,
        datasetId: string,
        trainingResultId: string,
        selectedTrainingMethod?: TrainingMethod,
        selectedClasses?: string[] | undefined,
        areClassesOverlapping: boolean = false,
    ): Observable<{ workflowRunId: string }> {
        if (this.isSettingsJsonWorkflow(workflow.createdAt)) {
            const inputImages = imagesToSegmentUrls.map(inputImage => getFileNameWithExtension(inputImage));
            return this.createSegmentationSettingsFile$(workflow.id, inputImages, trainingResultId).pipe(
                switchMap(settingsUrl => {
                    const runSteps = this.prepareSegmentationWorkflowWithSettingsJson(workflow, modelUrl, configUrl, settingsUrl, imagesToSegmentUrls);
                    return this.startSegmentWithPreparedRunSteps$(
                        workflow,
                        runSteps,
                        segmentationName,
                        notifyWhenIsDone,
                        datasetId,
                        trainingResultId,
                        selectedTrainingMethod,
                        selectedClasses,
                        areClassesOverlapping,
                    );
                }),
            );
        } else {
            const runSteps = this.prepareSegmentationWorkflowWithInputs(workflow, imagesToSegmentUrls, modelUrl, configUrl);
            return this.startSegmentWithPreparedRunSteps$(
                workflow,
                runSteps,
                segmentationName,
                notifyWhenIsDone,
                datasetId,
                trainingResultId,
                selectedTrainingMethod,
                selectedClasses,
                areClassesOverlapping,
            );
        }
    }

    /**
     * New version of ML Segmentation workflow accepts all settings as input file
     * (with name settings.json) of first, workflow input module.
     */
    startSegmentByWorkflowIdWithSettingsJson$(
        workflowId: string,
        modelUrl: string,
        configUrl: string,
        settingsUrl: string,
        imagesToSegmentUrls: string[],
        segmentationName: string,
        notifyWhenIsDone: boolean,
        classes: string[],
        modelId?: string,
    ): Observable<{ workflowRunId: string }> {
        return this.workflowV1Service.getById(workflowId).pipe(
            switchMap(workflow => {
                const runSteps = this.prepareSegmentationWorkflowWithSettingsJson(workflow, modelUrl, configUrl, settingsUrl, imagesToSegmentUrls);
                return this.startSegmentWithPreparedRunSteps$(
                    workflow,
                    runSteps,
                    segmentationName,
                    notifyWhenIsDone,
                    undefined,
                    undefined,
                    undefined,
                    classes,
                    false,
                    modelId,
                );
            }),
        );
    }

    fetchPastTrainingWorkflowResults(searchParams: ListSearch, withFiles: boolean, access?: 'read'): Observable<ListResponse<TrainingResult>> {
        let params = new HttpParams()
            .set('page[number]', searchParams.pageNumber.toString())
            .set('page[size]', searchParams.pageSize.toString())
            .set('text', searchParams.searchQuery)
            .set('title', searchParams.searchQuery)
            .set('sort', searchParams.sortBy)
            .set('withFiles', withFiles);

        if (access) {
            params = params.set('access', access);
        }
        return this.http
            .get<ListResponse<TrainingResult>>(`${this.MACHINE_LEARNING_TRAINING_API_URL}/${this.resultsUrlPart}`, {
                headers: this.headers,
                params,
                context: withCache({ cache: true, bucket: this.buckets.get(MlCacheType.SEGMENTATION_TRAINING) }),
            })
            .pipe(
                map((resultModels: ListResponse<TrainingResult>) => {
                    resultModels.data.forEach(r => new TrainingResult(r));
                    return resultModels;
                }),
            );
    }

    fetchTrainingsWithSegmentationsByDatasetId(datasetId: string, pageNumber: number, pageSize: number): Observable<ListResponse<TrainingResult>> {
        const params = new HttpParams().set('page[number]', pageNumber.toString()).set('page[size]', pageSize.toString());
        return this.http.get<ListResponse<TrainingResult>>(`${this.MACHINE_LEARNING_TRAINING_API_URL}/${this.resultsUrlPart}/datasetId/${datasetId}`, {
            headers: this.headers,
            params,
            context: withCache({ cache: true, bucket: this.buckets.get(MlCacheType.MODELS_WITH_SEGMENTATIONS) }),
        });
    }

    fetchModelWithFilesFromResult(workflowRunId: string): Observable<ModelWithFilesAndDatasetName> {
        return this.http.get<ModelWithFilesAndDatasetName>(`${this.MACHINE_LEARNING_TRAINING_API_URL}/modelWithFiles/ByWorkflowRunId/${workflowRunId}`);
    }

    fetchSegmentationsWithoutTrainingsByDatasetId(datasetId: string, searchParams: ListSearch): Observable<ListResponse<SegmentationResult>> {
        const params = new HttpParams()
            .set('page[number]', searchParams.pageNumber.toString())
            .set('page[size]', searchParams.pageSize.toString())
            .set('text', searchParams.searchQuery)
            .set('sort', searchParams.sortBy)
            .set('hasTrainings', false)
            .set('datasetId', datasetId);

        return this.http.get<ListResponse<SegmentationResult>>(`${this.MACHINE_LEARNING_SEGMENTATION_API_URL}/${this.resultsUrlPart}`, {
            headers: this.headers,
            params,
            context: withCache({ cache: true, bucket: this.buckets.get(MlCacheType.SEGMENTATIONS_WITHOUT_MODEL) }),
        });
    }

    fetchPastSegmentationWorkflowResults(searchParams: ListSearch, withFiles: boolean, access?: 'read'): Observable<ListResponse<SegmentationResult>> {
        let params = new HttpParams()
            .set('page[size]', searchParams.pageSize)
            .set('page[number]', searchParams.pageNumber)
            .set('sort', searchParams.sortBy)
            .set('withFiles', withFiles)
            .set('title', searchParams.searchQuery)
            .set('text', searchParams.searchQuery);

        if (access) {
            params = params.set('access', access);
        }

        return this.http
            .get<ListResponse<SegmentationResult>>(`${this.MACHINE_LEARNING_SEGMENTATION_API_URL}/${this.resultsUrlPart}`, {
                headers: this.headers,
                params,
                context: withCache({ cache: true, bucket: this.buckets.get(MlCacheType.SEGMENTATION) }),
            })
            .pipe(
                map((resultModels: ListResponse<SegmentationResult>) => {
                    resultModels.data.forEach(r => new SegmentationResult(r));
                    return resultModels;
                }),
            );
    }

    getTrainingOutputResultById(resultId: string): Observable<TrainingOutputResult> {
        return this.http.get<TrainingOutputResult>(`${this.MACHINE_LEARNING_TRAINING_API_URL}/${this.resultsUrlPart}/${resultId}/output`, {
            headers: this.headers,
            params: this.readAccessParams,
        });
    }

    getSegmentationOutputResultById(resultId: string): Observable<SegmentationOutputResult> {
        return this.http.get<SegmentationOutputResult>(`${this.MACHINE_LEARNING_SEGMENTATION_API_URL}/${this.resultsUrlPart}/${resultId}/output`, {
            headers: this.headers,
            params: this.readAccessParams,
        });
    }

    // delete cache bucket on workflow finish event to get fresh data on next request
    invalidateCache(mlCacheType: MlCacheType): void {
        const bucket = this.buckets.get(mlCacheType);
        if (bucket) {
            this.httpCacheManager.delete(bucket);
        }
    }

    invalidateCacheOfSegmentations(): void {
        this.invalidateCache(MlCacheType.SEGMENTATIONS_WITHOUT_MODEL);
        this.invalidateCache(MlCacheType.MODELS_WITH_SEGMENTATIONS);
        this.invalidateCache(MlCacheType.SEGMENTATION);
    }
    invalidateCacheOfTrainings(): void {
        this.invalidateCache(MlCacheType.MODELS_WITH_SEGMENTATIONS);
        this.invalidateCache(MlCacheType.SEGMENTATION_TRAINING);
    }

    invalidateCacheOfAllSegmentationsAndTrainingsWithSegmentations(): void {
        this.invalidateCache(MlCacheType.MODELS_WITH_SEGMENTATIONS);
        this.invalidateCache(MlCacheType.SEGMENTATION);
    }

    getTrainingImagesUrlsAndAnnotationUrls(datasetFiles: DatasetFiles, classes: string[]): ImagesWithAnnotations {
        const annotationsDict: {
            classesDictionaryByTrainingImage: Map<string, string[]>;
            annotationUrlsDictionaryByTrainingImage: Map<string, string[]>;
        } = createAnnotationsDictsByTrainingImagesAndFilterByClasses(datasetFiles.annotations, classes);
        // keep only training files that have annotations for ALL selected classes
        const trainingImagesUrls: string[] = [];
        // prepare annotation urls for workflow module input
        const annotationUrls: string[] = [];
        // for each file check if it has annotations for all selected classes - if so submit it - otherwise not
        // also prepare annotation urls for workflow module input for relevant training images
        datasetFiles.files.forEach(file => {
            const trainingImageName = FileUtils.getFileNameWithoutExtension(file.name);
            trainingImagesUrls.push(file.token);
            if (hasAnnotationsForAllClasses(annotationsDict.classesDictionaryByTrainingImage.get(trainingImageName), classes)) {
                const annotationUrlsByTrainingImage = annotationsDict.annotationUrlsDictionaryByTrainingImage.get(trainingImageName);
                // add all annotation urls for current file to workflow module input
                if (annotationUrlsByTrainingImage) {
                    annotationUrls.push(...annotationUrlsByTrainingImage);
                }
            }
        });
        return { trainingImagesUrls, annotationUrls } as ImagesWithAnnotations;
    }

    getMeasurements$(): Observable<MLMeasurement[]> {
        return this.http.get<MLMeasurement[]>(`${this.MACHINE_LEARNING_MEASUREMENT_API_URL}`, {
            headers: this.headers,
        });
    }

    validateFiles$(datasetId: string, fileIds: string[]): Observable<ValidateDatasetFileResult[]> {
        return this.http.post<ValidateDatasetFileResult[]>(`${this.DATASETS_API_URL}/validate/files`, { datasetId, fileIds }, { params: new HttpParams() });
    }

    validateSegmentationFile$(
        segmentationType: string,
        validateSegmentationFileDto: { fileId: string; trainingId: string },
    ): Observable<ValidateSegmentationFileResult> {
        return this.http.post<ValidateSegmentationFileResult>(
            `${this.MACHINE_LEARNING_SEGMENTATION_API_URL}/validate/file/${segmentationType}`,
            validateSegmentationFileDto,
            { params: new HttpParams() },
        );
    }

    private isSettingsJsonWorkflow(createdAt: string | undefined): boolean {
        if (createdAt == null) {
            return false;
        }
        return new Date(createdAt) >= environment.workflowInputSettingsJsonReleaseDate;
    }

    private startTrainWithPreparedRunSteps$(
        workflow: WorkflowV1Run,
        runSteps: RunStep[],
        trainingName: string,
        notifyWhenIsDone: boolean,
        workflowType: WorkflowType,
        zenCompatible: boolean = false,
        datasetId?: string,
        selectedTrainingMethod?: TrainingMethod,
        selectedClasses?: string[] | undefined,
        areClassesOverlapping: boolean = false,
        trainingId?: string,
        datasetVersionId?: string,
    ): Observable<{ workflowRunId: string }> {
        // We need this because usually we start a workflow from Editor/Runner (and this stores the current workflow in the workflowStateService).
        // Since we start a workflow here without accessing Editor/Runner, we need to store this workflow manually.
        this.workflowStateService.storeWorkflow(workflow);

        return this.workflowRunService.startWorkflowRun(
            runSteps,
            false,
            trainingName,
            selectedClasses,
            areClassesOverlapping,
            selectedTrainingMethod,
            undefined,
            notifyWhenIsDone,
            zenCompatible,
            datasetId,
            workflowType,
            undefined,
            trainingId,
            datasetVersionId,
        );
    }

    private startSegmentWithPreparedRunSteps$(
        workflow: WorkflowV1Run,
        runSteps: RunStep[],
        segmentationName: string,
        notifyWhenIsDone: boolean,
        datasetId?: string,
        trainingResultId?: string,
        selectedTrainingMethod?: TrainingMethod,
        selectedClasses?: string[] | undefined,
        areClassesOverlapping?: boolean | undefined,
        modelId?: string,
    ): Observable<{ workflowRunId: string }> {
        // We need this because usually we start a workflow from Editor/Runner (and this stores the current workflow in the workflowStateService).
        // Since we start a workflow here without accessing Editor/Runner, we need to store this workflow manually.
        this.workflowStateService.storeWorkflow(workflow);

        return this.workflowRunService.startWorkflowRun(
            runSteps,
            false,
            segmentationName,
            selectedClasses,
            areClassesOverlapping,
            selectedTrainingMethod,
            trainingResultId,
            notifyWhenIsDone,
            undefined,
            datasetId,
            WorkflowType.SEGMENTATION,
            modelId,
        );
    }

    private createTrainingSettingsFile$(
        trainingWorkflowId: string,
        datasetId: string,
        unique_class: boolean,
        zen_compatible_mode: boolean,
        training_name: string,
        model_name: string | null,
        trainingId: string | undefined,
        classes: string[],
        addMoreTrainingTime: boolean = false,
    ): Observable<TrainingSettingsJson> {
        return this.http.post<TrainingSettingsJson>(
            `${WORKFLOW_SETTINGS_SERVICE_URL}/${trainingWorkflowId}/training/settings`,
            {
                datasetId,
                unique_class,
                zen_compatible_mode,
                classes,
                training_name,
                model_name,
                trainingId: trainingId,
                addMoreTrainingTime: addMoreTrainingTime,
            },
            { headers: this.headers },
        );
    }

    private createSegmentationSettingsFile$(segmentationWorkflowId: string, inputImages: string[], trainingResultId: string): Observable<string> {
        return this.http.post<string>(
            `${WORKFLOW_SETTINGS_SERVICE_URL}/${segmentationWorkflowId}/prediction/settings`,
            { images: inputImages, trainingId: trainingResultId },
            { headers: this.headers },
        );
    }

    private prepareSegmentationWorkflowWithInputs(workflow: WorkflowV1Run, datasetUrl: string[], modelUrl: string, configUrl: string): RunStep[] {
        // We need to clone the objects, because since they're coming from ngrx store, they are not mutable.
        const clonedWorkflow: WorkflowV1Run = clone(workflow);
        const runSteps: RunStep[] = getRunSteps(clonedWorkflow, workflow.usedModules);

        this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(datasetUrl, runSteps[0], 'file_list');

        // apeer ML step is present in ml workflow version 2.0
        const apeerMlStep: RunStep | undefined = this.domainFactory.findApeerMlStep(runSteps);

        // Put model into first moduleDefineInput
        // TODO: Check if moduleDefineInput is connected to module with input name 'trained model'
        // we need to keep backward compatibility so we search by different index if ml workflow version >= 2.0
        let firstModuleDefineInputStep: RunStep | undefined = this.domainFactory.findNthModuleDefineInputStep(runSteps, apeerMlStep ? 1 : 0);
        if (firstModuleDefineInputStep != null) {
            firstModuleDefineInputStep = this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(modelUrl, firstModuleDefineInputStep, 'file');
        }

        // Put configUrl into second moduleDefineInput
        // TODO: Check if moduleDefineInput is connected to module with input name 'files infos'
        // we need to keep backward compatibility so we search by different index if ml workflow version >= 2.0
        let secondModuleDefineInputStep: RunStep | undefined = this.domainFactory.findNthModuleDefineInputStep(runSteps, apeerMlStep ? 2 : 1);
        if (secondModuleDefineInputStep != null) {
            secondModuleDefineInputStep = this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(configUrl, secondModuleDefineInputStep, 'file');
        }

        return runSteps;
    }

    private prepareSegmentationWorkflowWithSettingsJson(
        workflow: WorkflowV1Run,
        modelUrl: string,
        configUrl: string,
        settingsUrl: string,
        inputImagesUrls: string[],
    ): RunStep[] {
        // We need to clone the objects, because since they're coming from ngrx store, they are not mutable.
        const clonedWorkflow: WorkflowV1Run = clone(workflow);
        const selectedModules = getSelectedModules(workflow);
        const runSteps: RunStep[] = getRunSteps(clonedWorkflow, selectedModules);

        this.workflowRunnerStepBuilderService.addFileUrlToSystemModule([modelUrl, configUrl, settingsUrl, ...inputImagesUrls], runSteps[0], 'file_list');

        return runSteps;
    }

    private prepareTrainingWorkflowWithInputs(
        workflow: WorkflowV1Run,
        workflowDefineInputImages: string[],
        moduleDefineInputImages: string[],
        selectedTrainingMethod: TrainingMethod,
        areClassesOverlapping: boolean = false,
        zenCompatible: boolean = false,
        continueTrainingSettings?: ContinueTrainingSettings,
    ): RunStep[] {
        // We need to clone the objects, because since they're coming from ngrx store, they are not mutable.
        const clonedWorkflow = clone(workflow);
        const runSteps: RunStep[] = getRunSteps(clonedWorkflow, workflow.usedModules);

        this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(workflowDefineInputImages, runSteps[0], 'file_list');

        let moduleDefineInputStep: RunStep | undefined = this.domainFactory.findFirstModuleDefineInputStep(runSteps);
        if (moduleDefineInputStep != null) {
            moduleDefineInputStep = this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(moduleDefineInputImages, moduleDefineInputStep, 'file_list');
        }

        // apeer ML step is present in ml workflow version >= 2.0
        const apeerMlStep: RunStep | undefined = this.domainFactory.findApeerMlStep(runSteps);

        if (continueTrainingSettings) {
            // we need to keep backward compatibility so we search by different index if ml workflow version >= 2.0
            let configUrlModuleDefineInputStep: RunStep | undefined = this.domainFactory.findNthModuleDefineInputStep(runSteps, apeerMlStep ? 3 : 1);
            if (configUrlModuleDefineInputStep) {
                configUrlModuleDefineInputStep = this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(
                    continueTrainingSettings.configUrl,
                    configUrlModuleDefineInputStep,
                    'file',
                );
            }
            // we need to keep backward compatibility so we search by different index if ml workflow version >= 2.0
            let modelUrlModuleDefineInputStep: RunStep | undefined = this.domainFactory.findNthModuleDefineInputStep(runSteps, apeerMlStep ? 1 : 2);
            if (modelUrlModuleDefineInputStep) {
                modelUrlModuleDefineInputStep = this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(
                    continueTrainingSettings.modelUrl,
                    modelUrlModuleDefineInputStep,
                    'file',
                );
            }
        }

        if (apeerMlStep != null) {
            apeerMlStep.input['additional_experiment_settings'] = this.prepareMlTrainingSettings(selectedTrainingMethod, areClassesOverlapping, zenCompatible);
        } else {
            const mlDataPreparationStep: RunStep | undefined = this.domainFactory.findMLDataPreparationStep(runSteps);
            if (mlDataPreparationStep != null) {
                mlDataPreparationStep.input['segmentation_type'] = selectedTrainingMethod;

                // 397418: Set Multichannel prediction:
                mlDataPreparationStep.input['multichannels_predictions'] = areClassesOverlapping;
            }

            const mlDatasetValidationStep: RunStep | undefined = this.domainFactory.findMLDatasetValidationStep(runSteps);
            if (mlDatasetValidationStep != null) {
                mlDatasetValidationStep.input['segmentation_type'] = selectedTrainingMethod;
            }
        }

        return runSteps;
    }

    private prepareMlTrainingSettings(selectedTrainingMethod: TrainingMethod, areClassesOverlapping: boolean = false, zenCompatible: boolean = false): string {
        return JSON.stringify({
            task_type: selectedTrainingMethod,
            unique_class: !areClassesOverlapping,
            zen_compatible_mode: zenCompatible,
        });
    }

    private prepareTrainingWorkflowWithSettingsJson(
        workflow: WorkflowV1Run,
        settingsUrl: string,
        inputImagesUrls: string[],
        continueTrainingSettings?: ContinueTrainingSettings,
    ): RunStep[] {
        // We need to clone the objects, because since they're coming from ngrx store, they are not mutable.
        const clonedWorkflow = clone(workflow);
        const runSteps: RunStep[] = getRunSteps(clonedWorkflow, workflow.usedModules);

        if (continueTrainingSettings) {
            this.workflowRunnerStepBuilderService.addFileUrlToSystemModule(
                [continueTrainingSettings.modelUrl, continueTrainingSettings.configUrl, settingsUrl, ...inputImagesUrls],
                runSteps[0],
                'file_list',
            );
        } else {
            this.workflowRunnerStepBuilderService.addFileUrlToSystemModule([settingsUrl, ...inputImagesUrls], runSteps[0], 'file_list');
        }

        return runSteps;
    }
}
